import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { allPageFaqs } from "@/lib/section-faqs";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_ORAL, drugAboutAndMainEntity, drugNodes } from "@/lib/drug-schema";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Es segura la fentermina?",
    a: "Sí, para pacientes seleccionados de manera apropiada y bajo supervisión médica; se receta desde hace más de 60 años. No es segura durante el embarazo ni para personas con enfermedad cardíaca, presión arterial alta no controlada, hipertiroidismo o glaucoma. Por eso hacemos una evaluación cuidadosa, vigilamos la presión arterial y programamos controles regulares en vez de limitarnos a renovar recetas.",
  },
  {
    q: "¿Cuánto peso puedo perder con fentermina o Contrave?",
    a: "En los estudios clínicos, la pérdida promedio es de aproximadamente 5 por ciento del peso corporal, unas 12 a 15 libras para una persona de 250 libras, aunque algunos pacientes pierden bastante más. Los resultados dependen mucho de combinar el medicamento con cambios de alimentación; por eso nuestro programa incluye apoyo de una dietista y no solo la receta.",
  },
  {
    q: "¿Funcionan tan bien como Ozempic o Zepbound?",
    a: "En promedio, no. En los estudios, los medicamentos GLP-1 producen aproximadamente entre dos y cuatro veces más pérdida de peso. Sin embargo, los medicamentos orales más antiguos cuestan mucho menos, suelen tener mejor cobertura del seguro y a veces son la opción médica más adecuada. Para algunos pacientes dan exactamente el resultado que necesitan; para otros son un punto de partida y podemos avanzar a otra opción si hace falta.",
  },
  {
    q: "¿Cuál es la diferencia entre la fentermina y el dietilpropión?",
    a: "Funcionan de manera parecida: ambos son supresores del apetito de la clase de los estimulantes. El dietilpropión suele ser más suave, con menos efectos sobre el sueño, el nerviosismo y la frecuencia cardíaca. La fentermina suele ser la primera opción; el dietilpropión es nuestra alternativa habitual cuando la fentermina no se tolera bien.",
  },
  {
    q: "¿Puedo obtener fentermina de un sitio de pérdida de peso por internet?",
    a: "Tenga cuidado. La fentermina es una sustancia controlada y recetarla correctamente exige una evaluación médica real, control de la presión arterial y seguimiento. Un profesional que nunca lo ha examinado no puede descartar de forma segura las condiciones cardiovasculares que vuelven peligrosos los estimulantes. Nuestras evaluaciones son completas y los seguimientos por telemedicina facilitan la atención continua.",
  },
  {
    q: "¿Qué medicamento para bajar de peso es adecuado para mí?",
    a: "Depende de si su principal dificultad es el hambre, los antojos o los hábitos, además de su historia médica, su seguro y sus metas. Esa decisión requiere una conversación, no un cuestionario. El Dr. Fitzer receta todos los medicamentos para bajar de peso aprobados por la FDA y también realiza cirugía bariátrica, por lo que puede recomendarle la opción con más probabilidades de funcionar para usted.",
  },
];

export const Route = createFileRoute("/es/medicamentos-orales-para-bajar-de-peso")({
  head: () => {
    const base = esHead("oralMedications");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    ...esWebPageSchema("oralMedications"),
                    ...drugAboutAndMainEntity(ES_ORAL, [
                      { "@id": `${SITE}/es/medicamentos-orales-para-bajar-de-peso#tratamiento` },
                    ]),
                  },
                  ...drugNodes(ES_ORAL),
                  {
                    "@type": "FAQPage",
                    "@id": `${SITE}/es/medicamentos-orales-para-bajar-de-peso#preguntas`,
                    inLanguage: "es-US",
                    mainEntity: allPageFaqs(
                      "/es/medicamentos-orales-para-bajar-de-peso",
                      FAQS,
                    ).map((faq, index) => ({
                      "@type": "Question",
                      ...(index < FAQS.length
                        ? {
                            "@id": `${SITE}/es/medicamentos-orales-para-bajar-de-peso#pregunta-${index + 1}`,
                          }
                        : {}),
                      name: faq.q,
                      acceptedAnswer: { "@type": "Answer", text: faq.a },
                    })),
                  },
                  {
                    "@type": "MedicalTherapy",
                    "@id": `${SITE}/es/medicamentos-orales-para-bajar-de-peso#tratamiento`,
                    name: "Tratamiento oral contra la obesidad",
                    alternateName: ["Fentermina", "Contrave", "Orlistat", "Dietilpropión"],
                    description:
                      "Tratamiento médico supervisado con medicamentos orales contra la obesidad que actúan sobre el apetito, los antojos o la absorción de grasa.",
                    url: `${SITE}/es/medicamentos-orales-para-bajar-de-peso`,
                    recognizingAuthority: {
                      "@type": "MedicalClinic",
                      "@id": `${SITE}/#organization`,
                      name: "Virginia Bariatric Surgery",
                      url: `${SITE}/`,
                      telephone: "+1-703-709-9771",
                      address: {
                        "@type": "PostalAddress",
                        streetAddress: "171 Elden Street, Suite 3B",
                        addressLocality: "Herndon",
                        addressRegion: "VA",
                        postalCode: "20170",
                        addressCountry: "US",
                      },
                    },
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: EsOralMedications,
});

function EsOralMedications() {
  return (
    <EsPageShell
      page="oralMedications"
      title="Fentermina, Contrave® y otros medicamentos orales para bajar de peso"
      intro="Opciones eficaces y económicas que no son GLP-1, recetadas por un cirujano bariátrico"
    >
      <EsSection heading="Alternativas a los medicamentos inyectables">
        <p>
          Los medicamentos GLP-1 acaparan los titulares, pero no son la mejor opción para todos los
          pacientes.
        </p>
        <p>
          Hay varias razones para buscar alternativas a los inyectables. La falta de cobertura del
          seguro es bastante común. Un pequeño porcentaje de pacientes tiene una condición médica
          que vuelve inseguras las inyecciones, y cerca del 10 al 15 por ciento las deja por sus
          efectos secundarios.
        </p>
        <p>
          En un número considerable de pacientes, los inyectables producen poca o ninguna pérdida
          de peso. La respuesta a cualquier medicamento varía de una persona a otra, y la mitad de
          la población responderá por debajo del promedio a un medicamento determinado.
        </p>
        <p>
          La buena noticia es que existen alternativas. La FDA ha aprobado varios medicamentos
          orales —fentermina, dietilpropión, Contrave<sup>®</sup> y orlistat— que tienen décadas de
          experiencia clínica, cuestan mucho menos que los inyectables y pueden producir una pérdida
          de peso significativa cuando se recetan con cuidado y se acompañan de apoyo real.
        </p>
        <p>
          En Virginia Bariatric Surgery, en Herndon, el Dr. Matthew Fitzer, cirujano bariátrico
          certificado y con formación especializada, lleva más de dos décadas recetando toda la
          gama de medicamentos para bajar de peso. Lo ayudará a entender qué opciones se ajustan
          mejor a su cuerpo, su historia médica y su presupuesto. Atendemos a pacientes de Herndon,
          Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn, Chantilly y Manassas, además de
          toda el área metropolitana de Washington,
          D.C., con consultas por telemedicina disponibles en Virginia, Maryland y otros estados.
        </p>
      </EsSection>

      <EsSection id="por-que-considerarlos" heading="¿Por qué considerar un medicamento que no sea GLP-1?">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Costo.</strong> Los cuatro medicamentos de esta página están disponibles como
            genéricos económicos o mediante programas de ahorro del fabricante; a menudo cuestan
            menos de $30–$60 al mes, frente a cientos de dólares por los GLP-1 sin cobertura.
          </li>
          <li>
            <strong>Cobertura.</strong> Muchos planes que excluyen la semaglutida (Wegovy
            <sup>®</sup>) o la tirzepatida (Zepbound<sup>®</sup>) aún cubren las opciones orales más
            antiguas o las hacen asequibles.
          </li>
          <li>
            <strong>Mecanismos diferentes.</strong> Los antojos, la dificultad con el tamaño de las
            porciones y la absorción de grasa alimentaria son problemas distintos, y cada
            medicamento actúa sobre uno de ellos.
          </li>
        </ul>
        <p>
          La pérdida de peso promedio con estos medicamentos es más modesta que con los GLP-1:
          normalmente entre 3 y 8 por ciento del peso corporal frente a entre 15 y 21 por ciento, y
          se lo explicamos con claridad. Para muchos pacientes es suficiente; para otros, estos
          medicamentos son un punto de partida o una herramienta de mantenimiento dentro de un
          <Link
            to="/es/perdida-de-peso-con-medicamentos"
            className="font-medium text-brand-logo hover:underline"
          >
            programa integral
          </Link>.
        </p>
        <p>
          Cada persona responde de manera distinta a cada medicamento. Responder poco a los
          inyectables no significa que usted no pueda responder muy bien a otra opción. A menudo,
          los resultados con los medicamentos más antiguos sorprenden gratamente a los pacientes.
        </p>
      </EsSection>

      <EsSection id="comparacion" heading="Comparación rápida de las opciones" maxWidth="7xl">
        <p>
          *Resultados promedio de estudios clínicos durante 6 a 12 meses, junto con cambios de
          alimentación y estilo de vida; los resultados individuales varían.
        </p>
        <section className="overflow-x-auto" aria-label="Comparación de medicamentos orales para bajar de peso">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="px-3 py-2 text-left font-semibold">Medicamento</th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">Cómo funciona</th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">Resultados habituales*</th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">Más adecuado para</th>
              </tr>
            </thead>
            <tbody className="text-foreground/80">
              <tr className="border-b border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  Fentermina (Adipex-P<sup>®</sup>, Lomaira<sup>®</sup>)
                </th>
                <td className="px-3 py-2">Supresor del apetito de la clase de los estimulantes</td>
                <td className="px-3 py-2">Aproximadamente 5% del peso corporal</td>
                <td className="px-3 py-2">Hambre intensa, dificultad con las porciones y presupuesto limitado</td>
              </tr>
              <tr className="border-b border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  Dietilpropión (antes Tenuate<sup>®</sup>)
                </th>
                <td className="px-3 py-2">Supresor del apetito estimulante más suave</td>
                <td className="px-3 py-2">Aproximadamente 3–5% del peso corporal</td>
                <td className="px-3 py-2">Quienes necesitan una alternativa más suave a la fentermina</td>
              </tr>
              <tr className="border-b border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  Contrave<sup>®</sup> (naltrexona / bupropión)
                </th>
                <td className="px-3 py-2">Actúa en el cerebro sobre los antojos y la alimentación por recompensa</td>
                <td className="px-3 py-2">Aproximadamente 5–6% del peso corporal</td>
                <td className="px-3 py-2">Comer por emociones, antojos y pensamientos constantes sobre la comida; personas que también controlan el ánimo bajo o dejan de fumar</td>
              </tr>
              <tr className="border-b border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  Orlistat (Xenical<sup>®</sup>, Alli<sup>®</sup> sin receta)
                </th>
                <td className="px-3 py-2">Bloquea la absorción de cerca del 25–30% de la grasa alimentaria</td>
                <td className="px-3 py-2">Aproximadamente 3–5% del peso corporal</td>
                <td className="px-3 py-2">Quienes no pueden tomar estimulantes ni medicamentos que actúan en el cerebro</td>
              </tr>
            </tbody>
          </table>
        </section>

        <h3 className="mt-6 font-serif text-xl text-brand-mid">
          Fentermina: el medicamento para bajar de peso más recetado en Estados Unidos
        </h3>
        <p>
          La FDA aprobó la fentermina en 1959 y sigue siendo el medicamento para bajar de peso más
          recetado en Estados Unidos porque funciona y es económico. Reduce el apetito al estimular
          la liberación de norepinefrina en el cerebro, lo que facilita comer porciones más pequeñas
          sin sentirse privado. Se toma una vez al día, normalmente por la mañana.
        </p>
        <p>
          La fentermina es una sustancia controlada de la Lista IV aprobada para uso a corto plazo.
          Aunque algunos médicos prolongan el tratamiento fuera de indicación con vigilancia
          estrecha, exige evaluación en persona, controles de presión arterial y seguimiento
          regular. Los efectos secundarios comunes incluyen boca seca, insomnio y aumento de la
          frecuencia cardíaca o la presión arterial. No es apropiada durante el embarazo ni para
          personas con enfermedad cardíaca, hipertensión no controlada, hipertiroidismo o glaucoma.
          Este es un medicamento que se beneficia de una prescripción cuidadosa: precisamente el
          tipo de supervisión que ofrece un consultorio especializado en pérdida de peso quirúrgica.
        </p>

        <h3 className="mt-6 font-serif text-xl text-brand-mid">
          Dietilpropión: una alternativa estimulante más suave
        </h3>
        <p>
          Puede pensar en el dietilpropión como un pariente menos conocido de la fentermina: un
          supresor del apetito de la misma época y clase, generalmente más suave tanto en efecto como
          en efectos secundarios. Su principal diferencia es que dura mucho menos: unas seis horas,
          frente a las doce a dieciocho horas de la fentermina.
        </p>
        <p>
          Para quienes sienten demasiado nerviosismo, agitación o insomnio con la fentermina, el
          dietilpropión suele controlar el apetito de una manera más tolerable. Se toma como tableta
          de liberación prolongada de 75 mg una vez al día o en dosis de 25 mg antes de las comidas.
          Comparte las precauciones generales de la fentermina: es una sustancia controlada de la
          Lista IV, está aprobada para uso a corto plazo y no es adecuada para personas con
          enfermedad cardiovascular o presión arterial no controlada. Pocos consultorios del norte
          de Virginia la ofrecen, pero forma parte de nuestras opciones.
        </p>

        <h3 className="mt-6 font-serif text-xl text-brand-mid">
          Contrave<sup>®</sup>: para los antojos y la alimentación emocional
        </h3>
        <p>
          Contrave combina dos medicamentos de larga trayectoria, naltrexona y bupropión, que juntos
          actúan sobre las vías cerebrales del hambre y la recompensa. Mientras la fentermina reduce
          el hambre, Contrave suele ajustarse mejor cuando el problema son los <strong>antojos</strong>:
          comer por estrés, aburrimiento o costumbre y no por hambre física. En los estudios
          clínicos, los pacientes perdieron en promedio cerca del 5 al 6 por ciento del peso corporal
          durante un año, y aproximadamente 1 de cada 3 perdió 10 por ciento o más.
        </p>
        <p>
          Contrave no es una sustancia controlada y se toma en tabletas dos veces al día después de
          un aumento gradual de cuatro semanas. No debe usarse con opioides ni en personas con
          trastornos convulsivos, trastornos alimentarios o presión arterial alta no controlada.
          También lleva la advertencia estándar del bupropión sobre cambios del estado de ánimo, otra
          razón por la que importa el seguimiento médico. Un programa de ahorro del fabricante
          mantiene un costo mensual razonable para la mayoría de los pacientes, incluso sin
          cobertura del seguro.
        </p>

        <h3 className="mt-6 font-serif text-xl text-brand-mid">
          Orlistat: pérdida de peso sin efectos en el cerebro ni estimulantes
        </h3>
        <p>
          El orlistat funciona de una manera completamente distinta: permanece en el aparato
          digestivo y bloquea la enzima que digiere la grasa, de modo que entre una cuarta y una
          tercera parte de la grasa que usted come pasa sin absorberse. Como no entra en la sangre
          en cantidades significativas ni actúa sobre el cerebro, es una opción para quienes no
          pueden tomar estimulantes, medicamentos antidepresivos o GLP-1. Es el único medicamento de
          esta página con una versión sin receta, Alli<sup>®</sup>, a la mitad de la dosis recetada.
        </p>
        <p>
          La desventaja es predecible: una comida con mucha grasa puede causar heces aceitosas y
          urgencia intestinal; estos efectos también pueden enseñar de manera muy directa a reducir
          la grasa alimentaria. Acompañamos el orlistat con orientación de una dietista y un
          multivitamínico diario, porque puede reducir la absorción de las vitaminas A, D, E y K; el
          multivitamínico se toma a otra hora del día.
        </p>
      </EsSection>

      <EsSection id="como-elegimos" heading="Cómo elegimos el medicamento adecuado para usted">
        <p>
          No existe un único «mejor» medicamento para bajar de peso; existe el que mejor se ajusta a
          usted. Durante la evaluación revisamos:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong>Su forma de comer:</strong> el hambre constante apunta en una dirección; los antojos nocturnos y comer por estrés, en otra.</li>
          <li><strong>Su historia médica:</strong> presión arterial, salud cardíaca, medicamentos, salud mental e intentos anteriores de bajar de peso.</li>
          <li><strong>Su cobertura y presupuesto:</strong> buscamos la opción más eficaz que pueda mantener, sea un genérico de $25 o un GLP-1 cubierto.</li>
          <li>
            <strong>Sus metas:</strong> una persona que quiere perder 10 libras necesita una
            conversación diferente de quien quiere perder más de 100. Le diremos con honestidad si
            la <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">cirugía bariátrica</Link> o un <Link to="/es/medicamentos-para-bajar-de-peso" className="font-medium text-brand-logo hover:underline">medicamento GLP-1</Link> podría servirle mejor.
          </li>
        </ul>
        <p>
          Los medicamentos también se pueden alternar o combinar con el tiempo; por ejemplo, se
          puede usar uno oral para mantenimiento después de un GLP-1 o mientras se espera la
          autorización del seguro para otro tratamiento. Como el Dr. Fitzer receta todas estas
          opciones y también realiza cirugía bariátrica, usted recibe un solo plan y no un argumento
          de venta.
        </p>
      </EsSection>

      <EsSection id="costo-y-seguro" heading="Costo y seguro médico">
        <p>
          Aquí es donde se destacan los medicamentos más antiguos. La fentermina, el dietilpropión y
          el orlistat genéricos suelen costar menos que una suscripción de streaming cada mes en las
          farmacias del norte de Virginia, con o sin seguro, y el programa de ahorro de Contrave lo
          mantiene asequible para la mayoría de los pacientes. No hay batallas de autorización
          previa, escasez ni riesgos de farmacias de compuestos. Su consulta también puede estar
          cubierta aunque un medicamento específico no lo esté. Consulte nuestra guía de{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            cobertura de seguro
          </Link>{" "}
          o llame al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            (703) 709-9771
          </a>{" "}
          y lo ayudaremos a aclararlo antes de su primera cita.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes sobre los medicamentos orales">
        <dl className="space-y-4">
          {FAQS.map((faq, index) => (
            <div key={faq.q} id={`pregunta-${index + 1}`}>
              <dt className="font-medium text-brand-deep">{faq.q}</dt>
              <dd className="mt-1">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="siguiente-paso" heading="Siguiente paso: encuentre el medicamento que se ajusta a su vida">
        <p>
          No tiene que adivinar qué medicamento le conviene ni necesita el presupuesto de un GLP-1
          para obtener resultados reales. Llame al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            (703) 709-9771
          </a>{" "}
          o use nuestra página de{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contacto para pedir una cita
          </Link>{" "}
          con el Dr. Fitzer. Atendemos a Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria,
          Sterling, Ashburn, Chantilly y Manassas, además de toda el área metropolitana de Washington, D.C.;
          hay telemedicina disponible en Virginia.
        </p>
      </EsSection>
    </EsPageShell>
  );
}